import {
  ActionIcon,
  Button,
  Select,
  Table,
  Text,
  Textarea,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useMemo, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";

import Cell from "../../../../generator/Cell/Cell";
import { useGetApi } from "../../../../hooks/useGetApi";
import { getYachtId } from "../../../../utils";
import { DashboardCardContainer } from "./DashboardWidget.styled";
import { IUser } from "../../../shared/UserList/UserList.types";

export interface IHeader {
  name: string;
  unit?: string;
}

export interface IUserSnapshot {
  userid: string;
  firstname?: string;
  surname?: string;
  full_name?: string;
  rolename: string;
}

export interface ISection {
  id?: string;
  title?: string;
  subtitle?: string;
  headers: IHeader[];
  rows: any[];
  dynamicRows: boolean;
}

interface Props {
  lbid: string;
  sections?: ISection[];
  users: IUser[];
  onAutoSave?: (vals: any) => void;
  onDeleteRow?: (sectionIndex: number, rowIndex: number) => Promise<void>;
}

export default function WidgetLogbook({
  lbid,
  sections = [],
  users,
  onAutoSave,
  onDeleteRow,
}: Props) {
  const { watch, getValues, setValue, control } = useFormContext();
  const currentUserId = useSelector((s: any) => s.auth?.user?.id ?? "");

  // Helper function to create user snapshot supporting both formats
  const createUserSnapshot = (user: IUser): IUserSnapshot => {
    return {
      userid: user.userid,
      firstname: user.full_name.split(" ")[0] || "",
      surname: user.full_name.split(" ")[1] || "",
      full_name: user.full_name, // Keep for backward compatibility
      rolename: user.rolename,
    };
  };

  useEffect(() => {
    if (users.length === 0) return;

    sections.forEach((propSec, idx) => {
      const formRows = (getValues(`sections.${idx}.rows`) as any[]) || [];
      if (formRows.length > 0) return;

      const isUserSection =
        propSec.headers[0]?.unit === "time" ||
        (propSec.title?.toLowerCase().includes("watchkeeper") ?? false);

      if (isUserSection) {
        const now = dayjs().format("HH:mm");
        // Find current user object
        const currentUser = users.find(u => u.userid === currentUserId);
        const userSnapshot = currentUser
          ? createUserSnapshot(currentUser)
          : null;

        setValue(`sections.${idx}.rows`, [[now, userSnapshot, ""]], {
          shouldValidate: true,
        });
      }
    });
  }, [sections, getValues, setValue, currentUserId, users]);

  const ulist: IUser[] = [];
  const approve: Record<string, boolean> = {
    "1st Engineer": true,
    "2nd Engineer": true,
    "3rd Engineer": true,
    "4th Engineer": true,
    "Chief Engineer": true,
    "Chief Engineer 2": true,
    "Duty Engineer": true,
  };

  users.forEach((u: IUser) => {
    if (approve[u.rolename]) {
      ulist.push(u);
    }
  });
  const usersList = useMemo(
    () => ulist.map(u => ({ label: u.full_name, value: u.userid })) ?? [],
    [users],
  );

  const filteredSections = sections
    .map((sec, originalIdx) => ({ sec, originalIdx }))
    .filter(
      ({ sec }) =>
        sec.headers[0]?.unit === "time" ||
        sec.headers[0]?.unit === "large/text",
    );

  return (
    <DashboardCardContainer>
      {filteredSections.map(({ sec, originalIdx }, secIdx) => {
        const isUserSection =
          sec.headers[0]?.unit === "time" ||
          sec.headers[0]?.unit === "large/text";

        const rows = (watch(`sections.${originalIdx}.rows`) as any[]) || [];

        return (
          <div
            key={sec.id ?? originalIdx}
            style={{
              marginBottom: "2rem",
              background: "transparent",
            }}>
            {sec.title && (
              <Text
                align="center"
                weight={700}
                size="lg"
                style={{ marginBottom: 10, marginTop: 8, letterSpacing: 0.5 }}>
                {sec.title}
              </Text>
            )}
            {sec.subtitle && (
              <Text color="dimmed" align="center" style={{ marginBottom: 6 }}>
                {sec.subtitle}
              </Text>
            )}

            <div
              style={{
                maxWidth: "100%",
                overflowX: "auto",
              }}>
              <Table
                withBorder
                highlightOnHover
                styles={theme => ({
                  root: {
                    borderCollapse: "collapse",
                  },
                  th: {
                    backgroundColor: theme.colors.dark[5],
                    color: theme.colors.gray[1],
                    fontWeight: 500,
                    fontSize: 15,
                    padding: "8px 8px",
                    [`@media (max-width: 700px)`]: {
                      fontSize: 12,
                      padding: "6px 4px",
                    },
                  },
                  td: {
                    backgroundColor: theme.colors.dark[6],
                    color: theme.colors.gray[0],
                    fontSize: 15,
                    padding: "8px 8px",
                    [`@media (max-width: 700px)`]: {
                      fontSize: 12,
                      padding: "6px 4px",
                    },
                  },
                })}>
                <thead>
                  <tr>
                    <th
                      style={{
                        minWidth: 70,
                        maxWidth: 90,
                        width: "15%",
                        textAlign: "center",
                      }}>
                      Time
                    </th>
                    <th
                      style={{
                        minWidth: 100,
                        maxWidth: 170,
                        width: "25%",
                        textAlign: "center",
                      }}>
                      User
                    </th>
                    <th
                      style={{
                        width: "auto",
                        textAlign: "center",
                      }}>
                      Notes
                    </th>
                    {sec.dynamicRows && <th style={{ width: 40 }} />}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, rowIdx) => {
                    const timeVal = row[0] ?? "";
                    const userVal = row[1] ?? null;
                    const textVal = row[2] ?? "";

                    return (
                      <tr key={rowIdx}>
                        {/* TIME */}
                        <td
                          style={{
                            minWidth: 70,
                            maxWidth: 180,
                            textAlign: "center",
                          }}>
                          <Cell
                            unitType="time"
                            uuid={`sections.${originalIdx}.rows.${rowIdx}.0`}
                            cellId={`sections.${originalIdx}.rows.${rowIdx}.0`}
                            sectionIndex={originalIdx}
                            rowIndex={rowIdx}
                            colIndex={0}
                            cols={undefined}
                            flex=""
                            value={timeVal}
                            direction="row"
                            width="100%"
                            required={false}
                            maxWidth="150px"
                          />
                        </td>

                        {/* USER */}
                        <td
                          style={{
                            minWidth: 100,
                            maxWidth: 175,
                            textAlign: "center",
                          }}>
                          <Controller
                            control={control}
                            name={`sections.${originalIdx}.rows.${rowIdx}.1`}
                            defaultValue={userVal}
                            render={({ field }) => {
                              const selectedLabel = usersList.find(
                                u => u.value === userVal?.userid,
                              )?.label;
                              const fallbackLabel =
                                userVal?.firstname && userVal?.surname
                                  ? `${userVal.firstname} ${userVal.surname}`.trim()
                                  : userVal?.full_name || "";

                              return (
                                <Select
                                  value={userVal?.userid || ""}
                                  onChange={selectedUserId => {
                                    const selectedUser = users.find(
                                      u => u.userid === selectedUserId,
                                    );
                                    const userSnapshot = selectedUser
                                      ? createUserSnapshot(selectedUser)
                                      : null;
                                    field.onChange(userSnapshot);
                                  }}
                                  data={usersList}
                                  placeholder={fallbackLabel || "User"}
                                  searchValue={selectedLabel || fallbackLabel}
                                  nothingFound="—"
                                  dropdownPosition="top"
                                  withinPortal={true}
                                  zIndex={1000}
                                />
                              );
                            }}
                          />
                        </td>

                        {/* NOTES */}
                        <td style={{}}>
                          <Controller
                            control={control}
                            name={`sections.${originalIdx}.rows.${rowIdx}.2`}
                            defaultValue={textVal}
                            render={({ field }) => (
                              <Textarea
                                {...field}
                                autosize
                                minRows={1}
                                size="xs"
                                placeholder="Enter notes…"
                              />
                            )}
                          />
                        </td>

                        {/* DELETE ROW BUTTON */}
                        {sec.dynamicRows && (
                          <td
                            style={{
                              width: 40,
                              textAlign: "start",
                              verticalAlign: "middle",
                              paddingLeft: 4,
                              paddingRight: 8,
                            }}>
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="red"
                              disabled={rows.length <= 1}
                              onClick={async () => {
                                if (onDeleteRow && rows.length > 1) {
                                  await onDeleteRow(originalIdx, rowIdx);
                                } else {
                                  // Fallback to local state update only
                                  const newRows = [...rows];
                                  newRows.splice(rowIdx, 1);
                                  setValue(
                                    `sections.${originalIdx}.rows`,
                                    newRows,
                                    {
                                      shouldValidate: true,
                                    },
                                  );
                                }
                              }}>
                              <IconTrash size={20} />
                            </ActionIcon>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
            {sec.dynamicRows && isUserSection && (
              <Button
                size="xs"
                variant="outline"
                mt="xs"
                onClick={() => {
                  const now = dayjs().format("HH:mm");
                  const currentUser = users.find(
                    u => u.userid === currentUserId,
                  );
                  const userSnapshot = currentUser
                    ? createUserSnapshot(currentUser)
                    : null;

                  setValue(
                    `sections.${originalIdx}.rows`,
                    [...rows, [now, userSnapshot, ""]],
                    {
                      shouldValidate: true,
                    },
                  );
                }}>
                + Add row
              </Button>
            )}
          </div>
        );
      })}
    </DashboardCardContainer>
  );
}
